import React, { createContext, useState, useContext, useEffect } from 'react';
import { api, apiWithAuth } from '../lib/api';

// Development-only debug logging
const isDev = process.env.NODE_ENV === 'development';
const debugLog = (...args) => { if (isDev) console.log('[Auth]', ...args); };

const AuthContext = createContext();

// ユーザータイプ定義
const UserType = {
  JOB_SEEKER: 'job_seeker',
  EMPLOYER: 'employer'
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // ローカルストレージからトークンとユーザー情報を取得
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // 管理者のトークンを優先チェック
        const adminToken = localStorage.getItem('admin_token');
        const adminUserData = localStorage.getItem('admin_user');

        debugLog('Auth initialization - Admin Token exists:', !!adminToken);
        debugLog('Auth initialization - Admin User data exists:', !!adminUserData);

        // 管理者認証の場合（最優先）
        if (adminToken && adminUserData) {
          try {
            const parsedAdminUser = JSON.parse(adminUserData);
            debugLog('Parsed admin user data:', parsedAdminUser);

            // まずはローカルの管理者情報で即座に復元（CORS等で /me が失敗しても遷移を妨げない）
            const optimisticAdminUser = {
              id: parsedAdminUser.id,
              email: parsedAdminUser.admin_id,
              name: parsedAdminUser.name,
              user_type: 'admin',
              role: parsedAdminUser.role,
              permissions: parsedAdminUser.permissions
            };
            // 通常ユーザーの競合排除
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            setUser(optimisticAdminUser);
            setIsAuthenticated(true);
            debugLog('Admin authentication restored optimistically');

            // バックグラウンドで /admin/auth/me をバリデーション（失敗してもクリアしない）
            (async () => {
              try {
                const authApi = apiWithAuth(adminToken);
                const response = await authApi.get('/admin/auth/me');
                debugLog('Admin token background validation status:', response.status);
                if (response.status === 200) {
                  const currentAdminUser = response.data;
                  const confirmedAdminUser = {
                    id: currentAdminUser.id,
                    email: currentAdminUser.admin_id,
                    name: currentAdminUser.name,
                    user_type: 'admin',
                    role: currentAdminUser.role,
                    permissions: currentAdminUser.permissions
                  };
                  setUser(confirmedAdminUser);
                }
              } catch (e) {
                console.warn('Admin background validation failed (ignored):', e?.message || e);
                // NOTE: CORSやネットワークの一時的な問題は無視し、ナビゲーションを優先
              }
            })();

            setLoading(false);
            return;
          } catch (adminParseError) {
            console.error('Failed to parse admin user data:', adminParseError);
            // 破損している場合のみクリア
            localStorage.removeItem('admin_token');
            localStorage.removeItem('admin_user');
          }
        }

        // 一般ユーザーのトークンをチェック（管理者認証がない場合のみ）
        const token = localStorage.getItem('token');
        const userData = localStorage.getItem('user');

        debugLog('Auth initialization - Token exists:', !!token);
        debugLog('Auth initialization - User data exists:', !!userData);

        // 一般ユーザー認証の場合
        if (token && userData) {
          const parsedUser = JSON.parse(userData);
          debugLog('Parsed user data:', parsedUser);

          // 管理者データをクリア（競合防止）
          localStorage.removeItem('admin_token');
          localStorage.removeItem('admin_user');

          // トークンの有効性を確認
          const authApi = apiWithAuth(token);
          const response = await authApi.get('/auth/me');

          debugLog('Token validation response status:', response.status);

          if (response.status === 200) {
            let currentUser = response.data;

            // Admin check based on API response user_type only
            if (currentUser?.user_type === 'admin') {
              // Ensure admin tokens are restored if they were lost
              if (!localStorage.getItem('admin_token')) {
                localStorage.setItem('admin_token', token);
                localStorage.setItem('admin_user', JSON.stringify(currentUser));
              }
            }

            debugLog('Current user from API:', currentUser);
            setUser(currentUser);
            setIsAuthenticated(true);
          } else {
            debugLog('Token validation failed, clearing auth data');
            // 無効なトークンの場合はクリア
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            setUser(null);
            setIsAuthenticated(false);
          }
        } else {
          debugLog('No valid authentication found');
          // 全ての認証データをクリア
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          localStorage.removeItem('admin_token');
          localStorage.removeItem('admin_user');
          setUser(null);
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error('Auth initialization error (non-fatal):', error);
        // 管理者の既存セッションを不用意に破棄しない
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // ログイン関数
  const login = async (email, password) => {
    const isLocal = typeof window !== 'undefined' && (() => {
      const host = window.location.hostname || '';
      if (host === 'localhost' || host === '127.0.0.1' || host === '0.0.0.0') return true;
      if (/^10\./.test(host)) return true;
      if (/^192\.168\./.test(host)) return true;
      const m = host.match(/^172\.(\d{1,2})\./);
      if (m) { const n = parseInt(m[1], 10); if (n >= 16 && n <= 31) return true; }
      return false;
    })();

    const persistLogin = (data) => {
      const { access_token, user: userData } = data;
      // Prefer type hinted in token (e.g. devtoken:email:employer)
      let hintedType = null;
      try {
        if (typeof access_token === 'string' && access_token.startsWith('devtoken:')) {
          const parts = access_token.split(':');
          if (parts.length >= 3) hintedType = parts[2];
        }
      } catch { }
      // Fallback to pending_user_type when backend doesn't return or returns wrong type
      let pendingType = null;
      try { pendingType = localStorage.getItem('pending_user_type'); } catch { }

      let finalUserType = userData?.user_type || hintedType || pendingType || UserType.JOB_SEEKER;

      // Trust the user_type from backend response
      if (userData?.user_type === 'admin') {
        finalUserType = 'admin';
      }

      const effectiveUser = {
        ...userData,
        user_type: finalUserType,
      };
      try {
        localStorage.setItem('token', access_token);
        localStorage.setItem('user', JSON.stringify(effectiveUser));

        // Admin credentials for AdminRedirectGuard
        if (effectiveUser.user_type === 'admin') {
          debugLog('Admin user detected, setting admin_token');
          localStorage.setItem('admin_token', access_token);
          localStorage.setItem('admin_user', JSON.stringify(effectiveUser));
        }
      } catch { }
      setUser(effectiveUser);
      setIsAuthenticated(true);
      return { success: true, user: effectiveUser };
    };

    try {
      const response = await api.post('/auth/login', { email, password });
      if (response.status === 200) {
        debugLog('Login API successful, setting user state:', response.data?.user);
        return persistLogin(response.data);
      }
      return { success: false, error: response.data?.detail || 'ログインに失敗しました' };
    } catch (error) {
      const status = error?.response?.status;
      console.warn('Login error, considering fallbacks:', status, error?.message || error);

      // 環境に関わらず、まずはバックエンドの開発用ユーティリティにフォールバックを試みる
      try {
        const pendingType = localStorage.getItem('pending_user_type') || 'job_seeker';
        // 1) ユーザーがなければ即時作成して認証済みにする
        await api.post('/auth/dev-quick-setup', { email, password, user_type: pendingType });
        // 2) 再ログイン
        const retry = await api.post('/auth/login', { email, password });
        if (retry.status === 200) return persistLogin(retry.data);
      } catch (qe) {
        console.warn('dev-quick-setup + retry login failed:', qe?.message || qe);
        try {
          // 3) 認証済みメールだけでログイン
          const resp = await api.post(`/auth/dev-login-by-email?email=${encodeURIComponent(email)}`);
          if (resp.status === 200 && resp.data?.access_token && resp.data?.user) {
            return persistLogin(resp.data);
          }
        } catch (qe2) {
          console.warn('dev-login-by-email failed:', qe2?.message || qe2);
        }
      }

      // Return error for failed login attempts
      return { success: false, error: 'ネットワークエラーが発生しました' };
    }
  };

  // ユーザータイプに基づく適切なダッシュボードURLを取得
  const getDashboardUrl = (userType) => {
    switch (userType) {
      case UserType.JOB_SEEKER:
        // 求職者はマイページへ（ボトムナビあり）
        return '/mypage';
      case UserType.EMPLOYER:
        // 事業者は /business/dashboard へ
        return '/business/dashboard';
      case 'admin':
        return '/admin/dashboard';
      default:
        return '/';
    }
  };

  // 管理者ログイン関数
  const adminLogin = async (adminId, password) => {
    try {
      const response = await api.post('/admin/auth/login', { admin_id: adminId, password });
      const data = response.data;

      if (response.status === 200) {
        const { access_token, admin: adminData } = data;

        debugLog('Admin login successful, setting admin state:', adminData);

        // 管理者専用のトークンと情報を保存（通常ユーザートークンをクリア）
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.setItem('admin_token', access_token);
        localStorage.setItem('admin_user', JSON.stringify(adminData));

        // 管理者用の統一ユーザーオブジェクトを作成
        const adminUser = {
          id: adminData.id,
          email: adminData.admin_id,
          name: adminData.name,
          user_type: 'admin',
          role: adminData.role,
          permissions: adminData.permissions
        };

        // 状態を更新
        setUser(adminUser);
        setIsAuthenticated(true);

        debugLog('Admin state updated successfully');

        return { success: true, user: adminUser };
      } else {
        return { success: false, error: data.detail || '管理者ログインに失敗しました' };
      }
    } catch (error) {
      console.error('Admin login error:', error);
      return { success: false, error: 'ネットワークエラーが発生しました' };
    }
  };

  // ユーザー登録関数
  const register = async (userData) => {
    try {
      debugLog('Starting registration with data:', userData);

      // バックエンドの /auth/register は緊急措置により即時ログイン情報を返す場合あり
      const response = await api.post('/auth/register', userData);
      const data = response.data;
      debugLog('Registration response:', { status: response.status, data });

      if (response.status === 200 && (data?.ok || data?.message)) {
        // 即時ログインパス
        if (data?.access_token && data?.user) {
          try {
            localStorage.setItem('token', data.access_token);
            localStorage.setItem('user', JSON.stringify(data.user));
          } catch { }
          setUser(data.user);
          setIsAuthenticated(true);
          return { success: true, immediateLogin: true, user: data.user };
        }
        // メール認証完了後の自動ログインのため、一時的に資格情報を保存
        try {
          if (userData?.email) localStorage.setItem('pending_email', userData.email);
          if (userData?.password) localStorage.setItem('pending_password', userData.password);
          if (userData?.user_type) localStorage.setItem('pending_user_type', userData.user_type);
        } catch (e) {
          console.warn('Failed to store pending credentials:', e);
        }

        // 既存登録（already=true）の場合はログイン誘導
        if (data?.already) {
          return { success: true, already: true, message: data?.message || '既に登録済みです。ログインしてください。' };
        }

        // 正常にメール送信キュー投入
        return { success: true, message: data?.message || '認証メールを送信しました' };
      }

      console.error('Registration failed with response:', data);
      return { success: false, error: data?.detail || '登録に失敗しました' };
    } catch (error) {
      console.error('Registration network error:', error);
      return { success: false, error: 'ネットワークエラーが発生しました' };
    }
  };

  // ログアウト関数
  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    setUser(null);
    setIsAuthenticated(false);
  };

  // ユーザー情報更新関数
  const updateUser = (updatedUser) => {
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
  };

  // トークンを使用してログイン（メール確認用）
  const loginWithToken = async (token) => {
    try {
      const response = await api.get(`/auth/confirm?token=${token}`);
      const data = response.data;

      if (response.status === 200) {
        // トークンをローカルストレージに保存
        localStorage.setItem('token', data.access_token);

        // ユーザー情報を状態に保存
        const userData = {
          ...data.user,
          user_type: data.user.user_type || UserType.JOB_SEEKER,
        };

        setUser(userData);
        setIsAuthenticated(true);

        // ユーザー情報をローカルストレージに保存
        localStorage.setItem('user', JSON.stringify(userData));

        return { success: true, user: userData };
      } else {
        throw new Error(data.detail || 'トークンが無効です');
      }
    } catch (error) {
      console.error('Login with token error:', error);
      return { success: false, error: error.message };
    }
  };

  // 認証チェック関数
  const checkAuth = () => {
    const token = localStorage.getItem('token');
    return !!token && isAuthenticated;
  };

  // トークン取得関数
  const getToken = () => {
    return localStorage.getItem('token');
  };

  // 認証ヘッダー取得関数
  const getAuthHeaders = () => {
    const token = getToken();
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    adminLogin,
    register,
    loginWithToken,
    logout,
    updateUser,
    checkAuth,
    getToken,
    getAuthHeaders,
    getDashboardUrl,
    UserType
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// カスタムフック
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export { AuthContext };
export default AuthContext;